import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, mediaUrl } from "@/lib/api";

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

export function PlayerProvider({ children }) {
  const audio = useRef(null);
  if (!audio.current) audio.current = new Audio();
  const cur = useRef({ channel: null, track: null });
  const [channel, setChannel] = useState(null);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);

  const start = useCallback((t) => {
    const a = audio.current;
    a.src = mediaUrl(t.audio_url) + (t.offset > 0 ? `#t=${t.offset}` : "");
    a.play().catch(() => setPlaying(false));
    cur.current.track = t;
    setTrack(t);
    if (!t.is_message && !(t.show && t.offset > 0)) axios.post(`${API}/tracks/${t.id}/play`).catch(() => {});
  }, []);

  const next = useCallback(async () => {
    const ch = cur.current.channel;
    if (!ch) { setPlaying(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/channels/${ch.slug}/next`, { params: { exclude: cur.current.track?.id } });
      start(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos cargar el canal");
      setPlaying(false);
    } finally { setLoading(false); }
  }, [start]);

  const playChannel = useCallback((ch) => {
    if (cur.current.channel?.slug === ch.slug && audio.current.src && !cur.current.track?.show) { audio.current.play().catch(() => {}); return; }
    cur.current.channel = ch;
    setChannel(ch);
    next();
  }, [next]);

  const playSingle = useCallback((t) => {
    cur.current.channel = null;
    setChannel(null);
    start(t);
  }, [start]);

  const toggle = useCallback(() => {
    const a = audio.current;
    if (!a.src) return;
    if (a.paused) a.play().catch(() => {}); else a.pause();
  }, []);

  useEffect(() => {
    const a = audio.current;
    const on = () => setPlaying(true);
    const off = () => setPlaying(false);
    const time = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    a.addEventListener("play", on);
    a.addEventListener("pause", off);
    a.addEventListener("ended", next);
    const fail = () => {
      if (!a.src) return;
      toast.error("No se pudo reproducir esta canción");
      if (cur.current.channel) setTimeout(next, 1200); else setPlaying(false);
    };
    a.addEventListener("timeupdate", time);
    a.addEventListener("error", fail);
    return () => {
      a.removeEventListener("error", fail);
      a.removeEventListener("play", on);
      a.removeEventListener("pause", off);
      a.removeEventListener("ended", next);
      a.removeEventListener("timeupdate", time);
    };
  }, [next]);

  useEffect(() => { audio.current.volume = volume; }, [volume]);

  return (
    <PlayerCtx.Provider value={{ channel, track, playing, loading, volume, progress, setVolume, playChannel, playSingle, toggle, skip: next }}>
      {children}
    </PlayerCtx.Provider>
  );
}
